import React from "react";
import { useDropzone } from "react-dropzone";
import { Controller, useFormContext } from "react-hook-form";

import ButtonPrimitive from "@/components/primitives/Button";
import FieldLabel from "@/components/primitives/InputLabel";
import classNames from "@/helpers/classNames";
import { useToast } from "@/providers/ToastProvider";
import { DocumentPlusIcon } from "@heroicons/react/24/outline";

import FormTextInput from "./FormTextInput";
import Toggle from "@/components/elements/Toggle";

export default function SnowflakeForm({ editMode }: { editMode?: boolean }) {
  const { setValue, control, resetField } = useFormContext();

  const [useKeyPairAuthentication, setUseKeyPairAuthentication] =
    React.useState(false);
  return (
    <div>
      {editMode ? (
        <></>
      ) : (
        <>
          <FormTextInput
            field={{
              label: "Account",
              name: "account",
              type: "text",
              required: true,
              inputProps: {
                placeholder: "<identifier>.<region>.<cloud provider>",
              },
            }}
          />
          <FormTextInput
            field={{
              label: "Warehouse",
              name: "warehouse",
              type: "text",
              required: true,
              inputProps: {
                placeholder: "Warehouse",
              },
            }}
          />
        </>
      )}
      <FormTextInput
        field={{
          label: "Username",
          name: "username",
          type: "text",
          required: true,
          inputProps: {
            placeholder: "Username",
          },
        }}
      />
      <div className="mb-5 flex flex-row">
        <FieldLabel
          className="mb-0 text-sm font-medium"
          htmlFor="use-key-pair-authentication"
        >
          Use key pair authentication
        </FieldLabel>
        <Controller
          control={control}
          rules={{ required: false }}
          name="useKeyPairAuthentication"
          defaultValue={false}
          render={({ field: { onChange, onBlur, value } }) => (
            <Toggle
              checked={useKeyPairAuthentication}
              onChange={(checked: boolean) => {
                onChange(checked);
                resetField("password");
                resetField("privateKey");
                resetField("privateKeyPass");
                setUseKeyPairAuthentication(checked);
              }}
              id="use-key-pair-authentication"
              className="ml-auto"
            />
          )}
        ></Controller>
      </div>
      {useKeyPairAuthentication ? (
        <>
          <div className="mb-4">
            <FieldLabel required={true} id="payload">
              Upload your Snowflake Private Key
            </FieldLabel>
            <div className="mt-1">
              <Dropzone
                onChange={(privateKey) => {
                  setValue("privateKey", privateKey);
                }}
              />
            </div>
          </div>

          <FormTextInput
            field={{
              label: "Private key passphrase",
              name: "privateKeyPass",
              type: "text",
              required: false,
              inputProps: {
                placeholder: "Passphrase for the private key",
              },
            }}
          />
        </>
      ) : (
        <FormTextInput
          field={{
            label: "Password",
            description:
              "Password authentication is being deprecated. Use key pair authentication if possible.",
            name: "password",
            type: "password",
            required: true,
            inputProps: {
              placeholder: "Password",
            },
            descriptionType: "error",
          }}
        />
      )}
    </div>
  );
}

type DropzoneProps = {
  onChange: (privateKey: string) => void;
};

const Dropzone = ({ onChange }: DropzoneProps) => {
  const toast = useToast();
  const [filename, setFilename] = React.useState<string | null>(null);

  const onDrop = React.useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach((file) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result?.toString();
          if (
            result &&
            (result.includes("BEGIN PRIVATE KEY") ||
              result.includes("BEGIN ENCRYPTED PRIVATE KEY"))
          ) {
            onChange(result);
            setFilename(file.name);
          } else {
            setFilename(null);
            toast(
              "Invalid key file",
              <p>
                The provided private key file is not valid.
                <br />
                For the correct format, please refer to the{" "}
                <a
                  className="cursor-pointer underline"
                  target="_blank"
                  rel="noreferrer"
                  href="https://docs.snowflake.com/en/user-guide/key-pair-auth#generate-the-private-key"
                >
                  Snowflake documentation
                </a>
                .
              </p>,
              "error",
            );
          }
        };
        reader.readAsText(file, "UTF-8");
      });
    },
    [onChange, toast],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pkcs8": [".p8"],
    },
  });

  return (
    <div
      className={"mt-1 sm:col-span-2 sm:mt-0"}
      {...getRootProps()}
      role="button"
    >
      <input {...getInputProps()} />
      {!filename ? (
        <div
          className={classNames(
            "flex justify-center px-6 pb-6 pt-5",
            "rounded-md border-2 border-dashed",
            "hover:border-blue-200 hover:dark:border-blue-500",
            "hover:bg-blue-50 dark:hover:bg-blue-600/20",
            isDragActive &&
              "border-blue-200 bg-blue-50 dark:border-blue-500 dark:bg-blue-600/20",
          )}
        >
          <div className="space-y-1 text-center">
            <DocumentPlusIcon className="mx-auto h-12 w-12 text-gray-400" />
            <div className="text-sm text-gray-600">
              <span
                className={classNames(
                  "font-medium text-blue-500",
                  "focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2",
                )}
              >
                <span>Click to select your Snowflake private key file</span>
              </span>
              <p className="pl-1">or drag and drop</p>
            </div>
            <p className="text-xs text-gray-500">.P8</p>
          </div>
        </div>
      ) : (
        <ButtonPrimitive className="flex h-12 w-full items-center rounded border bg-background px-4 hover:bg-gray-100 dark:hover:bg-gray-700">
          <span className="truncate">{filename}</span>
        </ButtonPrimitive>
      )}
    </div>
  );
};
